import styled, { css } from 'styled-components'
import { Container } from 'src/components/common/Container'
import { Noto_body1_b16, Noto_body2_m14, Noto_body2_r14 } from 'src/styles/fonts/NotoSansKrFonts'
import { Typography } from 'src/components/common/Typography'
import React, { useEffect, useMemo, useState } from 'react'
import { CustomSwiper } from 'src/components/common/CustomSwiper'
import LazyImage from 'src/components/common/LazyImage'
import { ReactHookFc } from 'src/components/common/ReactHookForm'
import { useFormContext } from 'react-hook-form'
import { FormObjectType, GiftSendFormItemsType } from 'src/types/react-hook-form'
import { GiftMsgCardListSpace } from 'src/types/api'

const GiftSendMsgCard = ({
  registerAttribute,
  msgCardInfo,
  handleSelectMsgCard,
}: {
  msgCardInfo: GiftMsgCardListSpace.GiftMsgCardInfoType[] | undefined
  handleSelectMsgCard: (cardIdx: number, defaultMsg: string) => void
  registerAttribute: Partial<Record<keyof GiftSendFormItemsType, Partial<FormObjectType>>>
}) => {
  const { msg } = registerAttribute
  const { watch } = useFormContext<GiftSendFormItemsType>()

  const newMsg = watch('msg')
  const newCardIdx = watch('cardIdx')

  const msgCardCategoryList = useMemo(
    () => [
      { Group: 0, Title: '전체' },
      ...(msgCardInfo ?? [])?.map((el) => {
        return { Group: el.Group, Title: el.Title }
      }),
    ],
    [msgCardInfo],
  )
  const [activeMsgCardCategory, setActiveMsgCardCategory] = useState<number>(0)
  const selecedCategoryCardInfo = useMemo(
    () => msgCardInfo?.filter((el) => el.Group === activeMsgCardCategory),
    [msgCardInfo, activeMsgCardCategory],
  )
  const selectedCategoryCardList = useMemo(
    () => selecedCategoryCardInfo?.flatMap((el) => el.CardList ?? []),
    [selecedCategoryCardInfo],
  )
  const allCategoryCardList = useMemo(
    () => msgCardInfo?.flatMap((el) => el.CardList ?? []),
    [msgCardInfo],
  )
  const msgCardList = activeMsgCardCategory === 0 ? allCategoryCardList : selectedCategoryCardList
  const defaultMsg = useMemo(
    () => allCategoryCardList?.find((el) => el.CardIdx === newCardIdx)?.DefaultMsg,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [newCardIdx],
  )
  const activeImg = useMemo(
    () => allCategoryCardList?.find((el) => el.CardIdx === newCardIdx)?.Img,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [newCardIdx],
  )

  useEffect(() => {
    if (!!allCategoryCardList?.length && !newCardIdx)
      handleSelectMsgCard(allCategoryCardList?.[0]?.CardIdx, allCategoryCardList?.[0]?.DefaultMsg)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCategoryCardList])

  return (
    <S.GiftMsgCardContainer $option="flex_column" $row_gap="20px">
      <S.SectionTitleContainer>
        <Typography $fontStyle={Noto_body1_b16} $color="var(--black-90)">
          메시지 카드
        </Typography>
      </S.SectionTitleContainer>
      <S.GiftMsgImgContainer $option="flex_column" $row_gap="16px">
        {/* 카테고리 구분 필요시 주석 해제 */}
        {/*<CustomSwiper.Wrap type="default" $wrapstyle={S.GiftMsgCategorySwiper}>*/}
        {/*  {msgCardCategoryList?.map(*/}
        {/*    ({ Group, Title }: GiftMsgCardListSpace.GiftMsgCardInfoType) => (*/}
        {/*      <CustomSwiper.Item*/}
        {/*        key={Group}*/}
        {/*        $itemstyle={S.GiftMsgCategorySwiperItem(Group === activeMsgCardCategory)}*/}
        {/*        onClick={() => {*/}
        {/*          setActiveMsgCardCategory(Group)*/}
        {/*        }}*/}
        {/*      >*/}
        {/*        {Title}*/}
        {/*      </CustomSwiper.Item>*/}
        {/*    ),*/}
        {/*  )}*/}
        {/*</CustomSwiper.Wrap>*/}
        <CustomSwiper.Wrap type="default" $wrapstyle={S.GiftMsgImgSwiper}>
          {msgCardList?.map(
            ({ CardIdx, Img, DefaultMsg }: GiftMsgCardListSpace.GiftMsgCardItemType) => (
              <CustomSwiper.Item
                key={CardIdx}
                $itemstyle={S.GiftMsgImgSwiperItem(CardIdx === newCardIdx)}
                onClick={() => {
                  handleSelectMsgCard(CardIdx, DefaultMsg)
                }}
              >
                <LazyImage src={Img} alt="메시지 카드" />
              </CustomSwiper.Item>
            ),
          )}
        </CustomSwiper.Wrap>
      </S.GiftMsgImgContainer>
      <S.GiftMsgPreviewContainer $option="flex_column" $row_gap="12px">
        <LazyImage src={activeImg} alt="선택된 메시지 카드" style={S.GiftMsgPreviewImg()} />
        <S.GiftMsgTextContainer $option="flex_column" $row_gap="6px">
          <Typography
            $fontStyle={Noto_body2_m14}
            $color="var(--black-50)"
            $style={S.FormTextAreaCount()}
          >
            {newMsg.length}/100
          </Typography>
          <ReactHookFc.TextArea {...msg} placeholder={defaultMsg} style={S.FormTextArea()} />
        </S.GiftMsgTextContainer>
      </S.GiftMsgPreviewContainer>
    </S.GiftMsgCardContainer>
  )
}

export default GiftSendMsgCard

const S = {
  GiftMsgCardContainer: styled(Container)`
    padding: 40px 0 24px;
  `,
  SectionTitleContainer: styled(Container)`
    padding: 0 20px;
  `,
  GiftMsgImgContainer: styled(Container)``,
  GiftMsgCategorySwiper: css`
    padding: 0 20px;
    width: 100%;
  `,
  GiftMsgCategorySwiperItem: (select: boolean) => css`
    ${Noto_body2_r14};
    padding: 6px 16px;
    border-radius: 100px;
    border: 1px solid ${select ? 'var(--black-90)' : 'var(--black-10)'};
    color: ${select ? 'var(--black-90)' : 'var(--black-50)'};

    &:not(:last-child) {
      margin-right: 8px;
    }
  `,
  GiftMsgImgSwiper: css`
    padding: 0 20px;
    width: 100%;
    height: 70px;
  `,
  GiftMsgImgSwiperItem: (select: boolean) => css`
    width: 70px;
    height: 70px;
    opacity: ${select ? 1 : 0.3};

    & img {
      border-radius: 10px;
    }
    &:not(:last-child) {
      margin-right: 10px;
    }
  `,
  GiftMsgPreviewContainer: styled(Container)`
    margin: 20px auto 0;
    max-width: 270px;
  `,
  GiftMsgPreviewImg: () => css`
    width: 100%;
    height: 100%;
    border-radius: 20px;
  `,
  GiftMsgTextContainer: styled(Container)``,
  FormTextArea: () => css`
    padding: 12px 16px;
    height: 120px;
    border: 1px solid var(--black-20);
    border-radius: 12px;
  `,
  FormTextAreaCount: () => css`
    text-align: right;
  `,
}
